import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Paper, TextField, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useDispatch } from "react-redux";
import AxiosInstance from "../../services/networkServices";
import { showToast } from "../../store/slices/common";
import { readMinSupportedBuild } from "./minSupportedBuild";

const PLATFORMS = [
  { key: "ios", label: "iOS" },
  { key: "android", label: "Android" },
];

const EMPTY_DRAFT = { ios: { version: "", build: "" }, android: { version: "", build: "" } };

const cardSx = {
  padding: { xs: "14px", sm: "20px" },
  borderRadius: "10px",
  boxShadow: "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
  boxSizing: "border-box",
};

const fieldSx = {
  "& .MuiInputBase-input": {
    fontSize: 16,
  },
};

const toDraft = (minSupportedBuild) => ({
  ios: {
    version: minSupportedBuild?.ios?.version || "",
    build: minSupportedBuild?.ios?.build != null ? String(minSupportedBuild.ios.build) : "",
  },
  android: {
    version: minSupportedBuild?.android?.version || "",
    build:
      minSupportedBuild?.android?.build != null ? String(minSupportedBuild.android.build) : "",
  },
});

const MinSupportedBuildCard = () => {
  const dispatch = useDispatch();
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get("/admin/settings/min-supported-build");
      if (response.data?.status) {
        setDraft(toDraft(response.data.data?.minSupportedBuild));
      }
    } catch (error) {
      console.error("Failed to load minimum supported build:", error);
      dispatch(
        showToast({
          color: "error",
          msg: "Could not load the minimum supported build.",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (platform, field, value) => {
    setDraft((current) => ({
      ...current,
      [platform]: { ...current[platform], [field]: value },
    }));
  };

  const handleSave = async () => {
    const body = readMinSupportedBuild(draft);
    if (!body) {
      dispatch(
        showToast({
          color: "error",
          msg: "Each platform needs a YY.MM.DD version and a version code, or leave both blank.",
        })
      );
      return;
    }

    try {
      setSaving(true);
      const response = await AxiosInstance.put("/admin/settings/min-supported-build", body);
      if (response.data?.status) {
        setDraft(toDraft(response.data.data?.minSupportedBuild));
        dispatch(
          showToast({
            color: "success",
            msg: "Minimum supported build saved.",
          })
        );
      } else {
        dispatch(
          showToast({
            color: "error",
            msg: response.data?.message || "Failed to save the minimum supported build.",
          })
        );
      }
    } catch (error) {
      console.error("Failed to save minimum supported build:", error);
      dispatch(
        showToast({
          color: "error",
          msg: error?.response?.data?.message || "Failed to save the minimum supported build.",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={cardSx}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: "1.05rem", sm: "1.25rem" } }}>
        Minimum supported build
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The build you enter still works. Anything older must update from the store. A newer version
        still works even if its version code is lower. Leave both fields blank to turn the gate off
        for that platform.
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
          mb: 2,
        }}
      >
        {PLATFORMS.map(({ key, label }) => (
          <Box key={key} sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <TextField
              label={`${label} version`}
              placeholder="26.10.07"
              value={draft[key].version}
              onChange={(event) => setField(key, "version", event.target.value)}
              disabled={loading || saving}
              sx={{ ...fieldSx, flex: "1 1 140px" }}
              inputProps={{ "aria-label": `${label} version` }}
            />
            <TextField
              label={`${label} version code`}
              placeholder={key === "ios" ? "20" : "160"}
              value={draft[key].build}
              onChange={(event) => setField(key, "build", event.target.value)}
              disabled={loading || saving}
              sx={{ ...fieldSx, flex: "1 1 120px" }}
              inputProps={{ "aria-label": `${label} version code`, inputMode: "numeric" }}
            />
          </Box>
        ))}
      </Box>
      <Button
        variant="contained"
        startIcon={<SaveIcon />}
        onClick={handleSave}
        disabled={loading || saving}
        sx={{ textTransform: "none", minHeight: { xs: 44, sm: 36 } }}
      >
        {saving ? "Saving..." : "Save cutoff"}
      </Button>
    </Paper>
  );
};

export default MinSupportedBuildCard;
